'use client';

import { useCallback } from 'react';
import { Logger } from '@shared/logger';
import { useToast } from './useToast';
import type { NotificationOptions } from '../types/NotificationOptions';
import type { NotificationVariant } from '../types/NotificationVariant';

/**
 * Maps NotificationVariant to Radix Toast variant
 * - error → destructive (red)
 * - all others → default (neutral)
 */
const variantToToastVariant: Record<NotificationVariant, 'default' | 'destructive'> = {
  info: 'default',
  success: 'default',
  warning: 'default',
  error: 'destructive',
};

/**
 * @group Hooks
 * @summary Platform-agnostic notification hook for web.
 * Wraps useToast to provide a consistent API across platforms.
 * Automatically logs errors when variant is 'error' and error details are provided.
 *
 * @example
 * ```tsx
 * const { notify } = useNotification();
 *
 * // Show error notification with automatic logging
 * notify({
 *   title: 'Error',
 *   message: 'Failed to save',
 *   variant: 'error',
 *   error: {
 *     error: appError,
 *     resource: 'user.profile',
 *     context: { userId: '123' }
 *   }
 * });
 *
 * // Show success notification (no logging)
 * notify({
 *   title: 'Success',
 *   message: 'Profile saved',
 *   variant: 'success'
 * });
 * ```
 */
export function useNotification() {
  const { toast } = useToast();

  const notify = useCallback((options: NotificationOptions) => {
    // Log error when variant is 'error' and error details are provided
    if (options.variant === 'error' && options.error) {
      const { error, resource, context } = options.error;
      Logger.error(options.title, {
        resource,
        errorCode: error.errorCode,
        ...context,
      }, new Error(error.errorMessage));
    }

    toast({
      title: options.title,
      description: options.message,
      variant: variantToToastVariant[options.variant],
    });
  }, [toast]);

  return { notify };
}
